import {useMemo} from 'react';
import translate from '@/utils/translate';
import type {CampaignApprovalStep} from '@/types/campaign';
import type {BaseFilterOption} from '@/types/models/creator-crm/filter';
import {getApprovalStepDisplayName} from '@/views/campaigns/constants/approvalSteps';

const POST_PRODUCTION_FILTER_ENABLED = false;

/**
 * Derives status filter options from the campaign's approval steps.
 * Returns static options (New, Changes requested, Approved, Overdue)
 * plus dynamic "Awaiting: X" options for each approval step.
 */
function useContentSubmissionStatusOptions(
  approvalSteps: CampaignApprovalStep[] = []
) {
  const statusOptions: BaseFilterOption[] = useMemo(() => {
    const options: BaseFilterOption[] = [
      {id: 'new', localeLabelKey: 'New', value: 'new'},
    ];

    approvalSteps.forEach((step) => {
      options.push({
        id: `awaiting_${step.step}`,
        localeLabelKey: translate('Awaiting: {departmentName}', {
          departmentName: getApprovalStepDisplayName(step.step),
        }),
        value: step.step,
      });
    });

    options.push(
      {
        id: 'rejected',
        localeLabelKey: 'Changes requested',
        value: 'rejected',
      },
      {id: 'accepted', localeLabelKey: 'Approved', value: 'accepted'},
      {id: 'overdue', localeLabelKey: 'Overdue', value: 'overdue'}
    );

    if (POST_PRODUCTION_FILTER_ENABLED) {
      options.push({
        id: 'has_post_production',
        localeLabelKey: 'Has post-production',
        value: 'has_post_production',
      });
    }

    return options;
  }, [approvalSteps]);

  return {
    statusOptions,
    isLoading: false,
  };
}

export default useContentSubmissionStatusOptions;
